import { useCallback, useEffect } from "react";
import PulseMobileCard from "@features/pulse/components/mobile/PulseMobileCard";
import PulseCloseButton from "@features/pulse/components/PulseCloseButton";
import PulseEndingScreen from "@features/pulse/components/PulseEndingScreen";
import PulsePagination from "@features/pulse/components/PulsePagination";
import { useGetAutoplayTimes } from "@features/pulse/hooks/useGetAutoplayTimes";
import { useGetRandomArbitrary } from "@features/pulse/hooks/useGetRandomArbitrary";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { PulsePageProps } from "@features/pulse/utils/types";
import { cn } from "@util/helpers";

type PulseMobileCarouselProps = Omit<
  PulsePageProps,
  "handleSetActiveArticle" | "offset"
>;

/** Mobile carousel component for pulse */
const PulseMobileCarousel = ({
  sortedPulseArticles,
  activeArticle,
  handleNavigateRightCard,
  handleNavigateLeftCard,
}: PulseMobileCarouselProps) => {
  const { sendCustomEvent } = useSendPulseGaData();
  const { randomNumber } = useGetRandomArbitrary();

  const reverseData = [...sortedPulseArticles].reverse();

  const handleMobileAutoPlay = useCallback(() => {
    sendCustomEvent(
      `mobile::auto play::${activeArticle}`,
      sortedPulseArticles[activeArticle - 1].title
    );

    handleNavigateRightCard();
  }, [
    activeArticle,
    sortedPulseArticles,
    handleNavigateRightCard,
    sendCustomEvent,
  ]);

  const {
    autoplayTotal,
    isAutoplayEnabled,
    setIsAutoplayEnabled,
    resetAutoplayLeft,
    handleSetIsAutoplayPaused,
  } = useGetAutoplayTimes({
    activeArticle,
    sortedPulseArticles,
    handleNavigateRightCard: handleMobileAutoPlay,
  });

  const isDisplayEndScreen = activeArticle === reverseData.length + 1;

  const handleUndoClick = () => {
    handleNavigateLeftCard();

    resetAutoplayLeft();
    sendCustomEvent(
      `mobile::click undo:${activeArticle}`,
      sortedPulseArticles?.[activeArticle - 1]?.title || ""
    );
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Disable body scroll when the Pulse carousel is displayed
    window.document.documentElement.style.setProperty(
      "overflow-y",
      "hidden",
      "important"
    );
  }, []);

  if (isDisplayEndScreen) {
    return <PulseEndingScreen randomNumber={randomNumber} />;
  }

  return (
    <>
      <div className="flex items-center justify-between px-6 py-2">
        <PulseCloseButton
          activeArticle={activeArticle}
          sortedPulseArticles={sortedPulseArticles}
          isDesktop={false}
        />

        <PulsePagination
          activeArticle={activeArticle}
          totalCards={reverseData.length}
        />

        <button
          className={cn(
            "font-poppins text-xs font-semibold tracking-2% text-white transition-opacity duration-150",
            { "pointer-events-none opacity-0": activeArticle === 1 }
          )}
          onClick={handleUndoClick}
        >
          Undo
        </button>
      </div>

      <div className="items grid h-[calc(100%_-_56px)] w-full flex-shrink-0 flex-grow grid-cols-1 grid-rows-1 place-items-center p-4 pb-8 pt-0 text-gray-850">
        {reverseData.map((article, index) => {
          return (
            <PulseMobileCard
              key={article.id}
              sortedPulseArticles={sortedPulseArticles}
              article={article}
              activeArticle={activeArticle}
              index={index}
              handleNavigateRightCard={handleNavigateRightCard}
              autoplayTotal={autoplayTotal}
              isAutoplayEnabled={isAutoplayEnabled}
              setIsAutoplayEnabled={setIsAutoplayEnabled}
              resetAutoplayLeft={resetAutoplayLeft}
              handleSetIsAutoplayPaused={(isPaused) => {
                handleSetIsAutoplayPaused(isPaused);
              }}
              handleNavigateLeftCard={handleNavigateLeftCard}
            />
          );
        })}
      </div>
    </>
  );
};

export default PulseMobileCarousel;
